import React, { useState } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { X, Building2, MapPin, Phone, Receipt, Sparkles } from "lucide-react";
import { useAppDispatch } from "../../../../Redux/Hooks";
import { createBranchAPI, updateBranchAPI } from "../../../../Redux/ApiCalls/Dashboard/BranchAPI";
import { getUserByToken } from "../../../../Redux/ApiCalls/Auth/login";
import { showErrorToast, showSuccessToast } from "../../../atoms/Utlis/Toast";
import { CreateBranchProfilePayload, BranchProfile } from "../../../../Data/branch.dto";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
} from "../EventCalendar/eventModalSharedStyles";

interface AddBranchModalProps {
  branch?: BranchProfile | null;
  onClose: () => void;
  onSuccess?: (savedBranch: BranchProfile) => void;
}

const GST_NUMBER_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: #fafafa;
  border-top: 1px solid rgba(23, 23, 23, 0.08);
  flex-shrink: 0;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`;

const FooterHint = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.5);
`;

const CancelBtn = styled.button`
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.15);
  background: #ffffff;
  color: #171717;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SaveBtn = styled.button`
  padding: 10px 22px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.9) 0%, rgba(206, 86, 176, 0.9) 100%);
  color: #ffffff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(201, 77, 170, 0.25);
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(201, 77, 170, 0.35);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FieldFull = styled.div`
  grid-column: 1 / -1;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  display: flex;
  align-items: center;
  gap: 6px;

  span.req {
    color: #e11d48;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  color: #171717;
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    border-color: #c94daa;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.12);
  }

  &::placeholder {
    color: rgba(23, 23, 23, 0.4);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  color: #171717;
  outline: none;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 70px;
  box-sizing: border-box;

  &:focus {
    border-color: #c94daa;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.12);
  }

  &::placeholder {
    color: rgba(23, 23, 23, 0.4);
  }
`;

const AddBranchModal: React.FC<AddBranchModalProps> = ({ branch, onClose, onSuccess }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const isEditing = Boolean(branch && branch.id);

  const [formData, setFormData] = useState<CreateBranchProfilePayload>({
    branch_name: branch?.branch_name || "",
    branch_address: branch?.branch_address || "",
    branch_city: branch?.branch_city || "",
    branch_state: branch?.branch_state || "",
    branch_pincode: branch?.branch_pincode || "",
    branch_country: branch?.branch_country || "",
    branch_phone: branch?.branch_phone || "",
    gst_number: branch?.gst_number || "",
  });

  const handleChange = (field: keyof CreateBranchProfilePayload, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "gst_number" ? value.toUpperCase() : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.branch_name.trim()) {
      showErrorToast("Branch Name is required");
      return;
    }
    if (!formData.branch_phone.trim() || !/^[0-9]{10}$/.test(formData.branch_phone.trim())) {
      showErrorToast("Please enter a valid 10-digit phone number");
      return;
    }
    if (!formData.branch_address.trim()) {
      showErrorToast("Branch Address is required");
      return;
    }
    if (!formData.branch_city.trim()) {
      showErrorToast("Branch City is required");
      return;
    }
    if (!formData.branch_state.trim()) {
      showErrorToast("Branch State is required");
      return;
    }
    if (!formData.branch_pincode.trim()) {
      showErrorToast("Branch Pincode is required");
      return;
    }
    if (!formData.gst_number.trim() || !GST_NUMBER_REGEX.test(formData.gst_number.trim())) {
      showErrorToast("Please enter a valid 15-digit GST Number (e.g. 27ABCDE1234F1Z5)");
      return;
    }

    setLoading(true);
    try {
      if (isEditing && branch) {
        const updatePayload = {
          id: branch.id,
          branch_name: formData.branch_name.trim(),
          branch_address: formData.branch_address.trim(),
          branch_city: formData.branch_city.trim(),
          branch_state: formData.branch_state.trim(),
          branch_pincode: formData.branch_pincode.trim(),
          branch_country: formData.branch_country.trim(),
          branch_phone: formData.branch_phone.trim(),
          gst_number: formData.gst_number.trim(),
        };
        const resultAction = await dispatch(updateBranchAPI(updatePayload));
        if (updateBranchAPI.fulfilled.match(resultAction)) {
          const updatedBranch = (resultAction.payload as any)?.data || resultAction.payload;
          if (onSuccess && updatedBranch) {
            onSuccess(updatedBranch);
          }
          onClose();
        } else {
          const errorMsg =
            typeof resultAction.payload === "string"
              ? resultAction.payload
              : (resultAction.payload as any)?.message || "Failed to update branch";
          showErrorToast(errorMsg);
        }
      } else {
        const resultAction = await dispatch(createBranchAPI(formData));
        if (createBranchAPI.fulfilled.match(resultAction)) {
          const createdBranch = (resultAction.payload as any)?.data || resultAction.payload;
          // Also refresh user details so the new branch is available in user-details_v2
          if (createdBranch?.id) {
            await dispatch(getUserByToken(createdBranch.id));
          } else {
            await dispatch(getUserByToken());
          }
          if (onSuccess && createdBranch) {
            onSuccess(createdBranch);
          }
          onClose();
        } else {
          const errorMsg =
            typeof resultAction.payload === "string"
              ? resultAction.payload
              : (resultAction.payload as any)?.message || "Failed to create branch";
          showErrorToast(errorMsg);
        }
      }
    } catch (err: any) {
      showErrorToast(err?.message || `Failed to ${isEditing ? "update" : "create"} branch`);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalShell onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <Sparkles size={13} />
                Branches
              </HeroBadge>
              <ModalTitle>{isEditing ? "Edit Branch" : "Add New Branch"}</ModalTitle>
              <ModalSubtitle>
                {isEditing
                  ? "Update details for this studio branch."
                  : "Add a new studio branch with dedicated address, phone, and GST details."}
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose} aria-label="Close modal">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <form onSubmit={handleSubmit}>
          <ModalBody style={{ padding: "20px 24px" }}>
            <FormGrid>
              <FieldGroup>
                <Label>
                  <Building2 size={14} color="#c94daa" /> Branch Name <span className="req">*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter branch name"
                  value={formData.branch_name}
                  onChange={(e) => handleChange("branch_name", e.target.value)}
                  required
                />
              </FieldGroup>

              <FieldGroup>
                <Label>
                  <Phone size={14} color="#c94daa" /> Phone Number <span className="req">*</span>
                </Label>
                <Input
                  type="tel"
                  placeholder="Enter phone number"
                  maxLength={10}
                  value={formData.branch_phone}
                  onChange={(e) => handleChange("branch_phone", e.target.value.replace(/\D/g, ""))}
                  required
                />
              </FieldGroup>

              <FieldFull>
                <FieldGroup>
                  <Label>
                    <MapPin size={14} color="#c94daa" /> Branch Address <span className="req">*</span>
                  </Label>
                  <TextArea
                    placeholder="Enter branch address"
                    value={formData.branch_address}
                    onChange={(e) => handleChange("branch_address", e.target.value)}
                    required
                  />
                </FieldGroup>
              </FieldFull>

              <FieldGroup>
                <Label>
                  City <span className="req">*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter city"
                  value={formData.branch_city}
                  onChange={(e) => handleChange("branch_city", e.target.value)}
                  required
                />
              </FieldGroup>

              <FieldGroup>
                <Label>
                  State <span className="req">*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter state"
                  value={formData.branch_state}
                  onChange={(e) => handleChange("branch_state", e.target.value)}
                  required
                />
              </FieldGroup>

              <FieldGroup>
                <Label>
                  Pincode <span className="req">*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter pincode"
                  maxLength={6}
                  value={formData.branch_pincode}
                  onChange={(e) => handleChange("branch_pincode", e.target.value)}
                  required
                />
              </FieldGroup>

              <FieldGroup>
                <Label>
                  Country <span className="req">*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter country"
                  value={formData.branch_country}
                  onChange={(e) => handleChange("branch_country", e.target.value)}
                  required
                />
              </FieldGroup>

              <FieldFull>
                <FieldGroup>
                  <Label>
                    <Receipt size={14} color="#c94daa" /> GST Number <span className="req">*</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter GST number"
                    maxLength={15}
                    value={formData.gst_number}
                    onChange={(e) => handleChange("gst_number", e.target.value)}
                    required
                  />
                </FieldGroup>
              </FieldFull>
            </FormGrid>
          </ModalBody>

          <ModalFooter>
            <FooterHint>All fields are required to setup the branch profile.</FooterHint>
            <div style={{ display: "flex", gap: "10px" }}>
              <CancelBtn type="button" onClick={onClose} disabled={loading}>
                Cancel
              </CancelBtn>
              <SaveBtn type="submit" disabled={loading}>
                {loading ? (isEditing ? "Saving..." : "Creating...") : (isEditing ? "Save Changes" : "Create Branch")}
              </SaveBtn>
            </div>
          </ModalFooter>
        </form>
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
};

export default AddBranchModal;
